import { useRef, useState } from "react";
import {
  X,
  Printer,
  Download,
  CheckCircle2,
  Building2,
  User,
  Calendar,
  CreditCard,
  ReceiptText,
  Loader2,
} from "@semo/icons";
import { Button, cn } from "@semo/shared-ui";
import { formatDateKorean, formatCurrency } from "@semo/shared-utils";
import type { CancellationRequestMessageResponseDto } from "@semo/api-contracts";
import { useContractsControllerDownloadCancellationPdf } from "@semo/api-contracts";
import { toast } from "sonner";

export interface CancellationReceiptModalProps {
  /** Cancellation request message data */
  message: CancellationRequestMessageResponseDto;
  /** Close callback */
  onClose: () => void;
  /** Context info for the receipt */
  context?: {
    projectName?: string;
    buyerName?: string;
    supplierName?: string;
    totalAmount?: number;
  };
}

export function CancellationReceiptModal({
  message,
  onClose,
  context,
}: CancellationReceiptModalProps) {
  const data = message.cancellationRequestData;
  const printRef = useRef<HTMLDivElement>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  // Contract ID might be in data or message
  const contractId = data?.contractId;

  const { refetch: downloadPdf } =
    useContractsControllerDownloadCancellationPdf(contractId || "", {
      query: {
        enabled: false,
      },
    });

  if (!data || !contractId) return null;

  const paymentMethod = (data as any).paymentMethod || "CARD";
  const isCash = paymentMethod === "CASH";
  const amount = context?.totalAmount || 0;
  const date = formatDateKorean(message.updatedAt || message.createdAt);

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = async () => {
    try {
      setIsGenerating(true);
      const result = await downloadPdf();

      if (result.data?.data?.pdfUrl) {
        window.open(result.data.data.pdfUrl, "_blank");
      } else if (result.isError && !(result.error.statusCode > 0)) {
        // 서버에 닿지 못했다(연결 끊김·시간 초과) — 매뉴얼 R06 5.2.4.4 4번.
        // refetch 는 실패해도 던지지 않으므로 여기서 갈라야 4번이 뜬다.
        toast.error("확인서 다운로드에 실패했습니다.");
      } else {
        // 서버가 확인서를 만들지 못했다 — 5.2.4.4 3번
        toast.error("PDF를 생성할 수 없습니다.");
      }
    } catch (error) {
      console.error("Failed to download cancellation PDF:", error);
      toast.error("확인서 다운로드에 실패했습니다.");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div
      className={cn(
        "fixed inset-0 z-[210]",
        "flex items-center justify-center",
        "p-2 sm:p-4",
        "bg-slate-900/60 backdrop-blur-sm",
        "animate-in fade-in duration-200",
      )}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className={cn(
          "bg-white",
          "w-full max-w-lg max-h-[90vh]",
          "rounded-2xl shadow-2xl",
          "flex flex-col overflow-hidden",
          "animate-in zoom-in-95 duration-300",
        )}
      >
        {/* Header Toolbar */}
        <div className="h-14 border-b border-slate-100 flex items-center justify-between px-6 bg-slate-50/50 shrink-0">
          <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <ReceiptText className="w-4 h-4 text-primary-text" />
            계약 취소 확인서
          </h2>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="w-8 h-8 hover:bg-slate-200 rounded-full"
              onClick={handlePrint}
              title="인쇄하기"
            >
              <Printer className="w-4 h-4 text-slate-600" />
            </Button>
            <button
              onClick={onClose}
              className="p-1 hover:bg-slate-200 rounded-full transition-colors"
              aria-label="닫기"
            >
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
        </div>

        {/* Receipt Content */}
        <div className="flex-1 overflow-y-auto p-6 sm:p-8 custom-scrollbar bg-slate-50/30">
          <div
            ref={printRef}
            className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 shadow-sm space-y-8 print:border-0 print:shadow-none print-area"
          >
            {/* Status & Amount */}
            <div className="text-center space-y-3 pb-8 border-b border-dashed border-slate-200">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-50 text-red-500 mb-2">
                <CheckCircle2 className="w-6 h-6" />
              </div>
              <p className="text-sm font-semibold text-red-600 uppercase tracking-wider">
                Cancellation Completed
              </p>
              <div className="space-y-1">
                <h3 className="text-3xl font-semibold text-slate-900 tracking-tight">
                  -{formatCurrency(amount)}
                  <span className="text-lg font-semibold ml-1">원</span>
                </h3>
                <p className="text-sm text-slate-500 font-medium">
                  전액 환불 완료
                </p>
              </div>
            </div>

            {/* Transaction Details */}
            <div className="space-y-4">
              <div className="flex justify-between items-start">
                <span className="text-sm font-medium text-slate-400">
                  프로젝트명
                </span>
                <span className="text-sm font-semibold text-slate-800 text-right max-w-[200px]">
                  {context?.projectName || "계약 서비스"}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-slate-400">
                  결제 수단
                </span>
                <div className="flex items-center gap-1.5">
                  {isCash ? (
                    <Building2 className="w-3.5 h-3.5 text-slate-400" />
                  ) : (
                    <CreditCard className="w-3.5 h-3.5 text-slate-400" />
                  )}
                  <span className="text-sm font-semibold text-slate-800">
                    {isCash ? "현금 결제 (계좌이체)" : "카드 결제"}
                  </span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-slate-400">
                  취소 일시
                </span>
                <div className="flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5 text-slate-400" />
                  <span className="text-sm font-semibold text-slate-800">
                    {date}
                  </span>
                </div>
              </div>
              {data.tid && (
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-slate-400">
                    거래 번호
                  </span>
                  <span className="text-xs font-mono text-slate-500">
                    {data.tid}
                  </span>
                </div>
              )}
            </div>

            {/* Parties Info */}
            <div className="grid grid-cols-2 gap-4 pt-6 border-t border-slate-100">
              <div className="space-y-1.5">
                <span className="text-2xs font-semibold text-slate-400 uppercase tracking-widest block">
                  판매자
                </span>
                <p className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
                  <Building2 className="w-3 h-3" />
                  {context?.supplierName || "공급자"}
                </p>
              </div>
              <div className="space-y-1.5 text-right">
                <span className="text-2xs font-semibold text-slate-400 uppercase tracking-widest block">
                  구매자
                </span>
                <p className="text-sm font-semibold text-slate-700 flex items-center justify-end gap-1.5">
                  {context?.buyerName || "구매자"}
                  <User className="w-3 h-3" />
                </p>
              </div>
            </div>

            {/* Footer / Legal */}
            <div className="pt-4 text-center">
              <p className="text-2xs text-slate-400 leading-relaxed">
                본 확인서는 결제가 정상적으로 취소되었음을 증빙합니다.
                <br />
                현금 결제의 경우 판매자 또는 관리자를 통해 환불 처리가
                진행됩니다.
              </p>
            </div>
          </div>

          {/* Action Button */}
          <div className="mt-6 space-y-3">
            <Button
              variant="outline"
              className="w-full h-12 rounded-xl border-slate-200 text-slate-600 font-semibold gap-2 hover:bg-slate-50"
              onClick={handleDownload}
              disabled={isGenerating}
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  생성 중...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  확인서 저장하기
                </>
              )}
            </Button>
            <p className="text-2xs text-slate-400 text-center">
              서버에서 생성된 정식 PDF 확인서를 다운로드합니다.
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          body * {
            visibility: hidden;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .print-area, .print-area * {
            visibility: visible;
          }
          .print-area {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 2rem !important;
            border: 1px solid #e2e8f0 !important;
            border-radius: 1rem !important;
          }
          .fixed {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
}
